/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import styled from "@emotion/styled";
import {
	Box,
	Icon,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	FormControl,
	Select,
	MenuItem
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import MDTypography from "components/Basics/MDTypography";
import { LittleForm } from "components/Custom/LittleForm";
import { t } from "i18next";
import moment from "moment";
import { useEffect, useState } from "react";
import { CustomFlag } from "utilities";
import { useDispatch, useSelector } from "react-redux";
import RenderAssetDisplay from "./RenderAssetDisplay";
import { createDictionarySkeleton } from "helpers/form";
import SourceActions from "redux-react/actions/sourceActions";
import { updateAsset } from "redux-react/reducers/assetReducer";
import { queryDictionary } from "helpers/dictionary";
import InputBoolean from "components/Custom/LittleForm/Inputs/InputBoolean";
import { useLanguages } from "hooks/useLanguages";

const ScrollableContent = styled(Box)({
	overflowY: "auto",
	flex: 1,
	paddingBottom: "16px"
});

const DetailItem = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "flex-start",
	marginBottom: theme.spacing(1),
	padding: theme.spacing(1),
	backgroundColor: theme.palette.grey[100],
	borderRadius: theme.shape.borderRadius
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
	fontWeight: theme.typography.fontWeightMedium,
	color: theme.palette.text.secondary,
	minWidth: "120px",
	fontSize: "0.9rem"
}));

const DetailValue = styled(Typography, {
	shouldForwardProp: prop => prop !== "isExpanded"
})(({ theme, isExpanded }) => ({
	flexGrow: 1,
	color: theme.palette.text.primary,
	fontSize: "0.9rem",
	wordBreak: "break-word",
	maxHeight: isExpanded ? "none" : "4.5rem",
	overflow: "hidden",
	transition: "max-height 0.3s ease-in-out",
	display: "-webkit-box",
	WebkitBoxOrient: "vertical",
	WebkitLineClamp: isExpanded ? "none" : 3,
	lineClamp: isExpanded ? "none" : 3,
	textOverflow: "ellipsis"
}));

const DetailContent = ({ asset, setLocalState }) => {
	const dispatch = useDispatch();
	const { languages } = useLanguages();

	const [editionDisabled, setEditionDisabled] = useState(true);
	const [currentLanguage, setCurrentLanguage] = useState("");

	const [metadatasDictionary, setMetadatasDictionary] = useState({});
	const [metadatasSkeleton, setMetadatasSkeleton] = useState({});

	const handleTabChange = event => {
		setCurrentLanguage(event.target.value);
	};

	const saveMetadatasChanges = () => {
		setEditionDisabled(true);

		const hasChanges = !lod_.isEqual(
			metadatasSkeleton,
			createDictionarySkeleton(metadatasDictionary, asset?.metadatas ?? {})
		);

		if (!lod_.isNil(asset) && !lod_.isNil(asset._id) && hasChanges) {
			dispatch(
				SourceActions.updateAsset(asset._id, { metadatas: metadatasSkeleton }, res => {
					setLocalState(prev => ({
						...prev,
						selectedAsset: { ...res, id: asset._id }
					}));
					dispatch(updateAsset({ ...res, id: asset._id }));
				})
			);
		}
	};
	const cancelMetadatasChanges = () => {
		setEditionDisabled(true);
		setMetadatasSkeleton(createDictionarySkeleton(metadatasDictionary, asset?.metadatas ?? {}));
	};

	const handleChange = (field, value) => {
		setMetadatasSkeleton(prev => {
			const copy = lod_.cloneDeep(prev);
			lod_.set(copy, field, value);
			return copy;
		});
	};

	const fetchMetadatasDictionary = async () => {
		const dictionary = await queryDictionary(dispatch, "metadatas");
		setMetadatasDictionary(dictionary);
		setMetadatasSkeleton(createDictionarySkeleton(dictionary, asset?.metadatas ?? {}));
	};

	const hasCustomMetadatas = items => {
		// Convert object to array if it's not already
		const itemsArray = Array.isArray(items) ? items : Object.values(items || {});

		// Check if there is any item system false
		return itemsArray.some(item => !item.system);
	};

	const hasAssetContentForLanguage = languageCode => {
		if (lod_.isNil(asset) || lod_.isNil(asset.content) || asset.content.length === 0) {
			return false;
		}
		const contentForLang = asset.content[0][languageCode.toUpperCase()];

		if (lod_.isNil(contentForLang) || Object.keys(contentForLang).length === 0) {
			return false;
		}

		if (lod_.has(contentForLang, "htmlText")) {
			if (contentForLang.htmlText.trim() === "" || contentForLang.htmlText === "<p></p>") {
				return false;
			}
		}

		return true;
	};

	useEffect(() => {
		if (!lod_.isNil(asset)) {
			fetchMetadatasDictionary();
		}
	}, []);

	useEffect(() => {
		if (!lod_.isNil(asset.metadatas)) {
			setMetadatasSkeleton(asset.metadatas);
		}
	}, [asset]);

	useEffect(() => {
		if (languages.length > 0 && (lod_.isNil(currentLanguage) || lod_.isEmpty(currentLanguage))) {
			// Get the first language from the asset
			const assetContent = asset?.content?.[0] || {};
			const notEmptyFirstKey = Object.keys(assetContent).find(key => {
				return hasAssetContentForLanguage(key);
			});
			// Check if the found key matches any language code
			if (!lod_.isNil(notEmptyFirstKey)) {
				const matchingLang = languages.find(
					lang => lang.code.toUpperCase() === notEmptyFirstKey.toUpperCase()
				);
				if (matchingLang) {
					setCurrentLanguage(matchingLang.code);
					return; // Exit the function if a match is found
				}
			}
			// If no match found, set to the first language in the list
			setCurrentLanguage(languages[0].code);
		}
	}, [languages]);

	return (
		<MDBox sx={{ flex: 1, p: 1, overflow: "auto" }}>
			{/* Language Select */}
			<FormControl sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
				<Select
					value={currentLanguage}
					onChange={handleTabChange}
					renderValue={value => {
						const lang = languages.find(l => l.code === value);
						return lang ? (
							<Stack direction="row" spacing={1} alignItems="center">
								<CustomFlag code={lang.code} style={{ height: "2vh", width: "2vh" }} />
								<MDTypography variant="subtitle2">{lang.name}</MDTypography>
							</Stack>
						) : null;
					}}
				>
					{languages?.map(lang => (
						<MenuItem
							key={lang?.code}
							value={lang.code}
							disabled={!hasAssetContentForLanguage(lang.code)}
						>
							<Stack
								direction="row"
								display="flex"
								alignItems="center"
								justifyContent="space-between"
								sx={{
									width: "100%"
								}}
							>
								<MDBox display="flex" alignItems="center" justifyContent="center">
									<CustomFlag
										code={lang.code}
										style={{
											height: "2vh",
											width: "2vh"
										}}
									/>
									<MDTypography sx={{ ml: 1 }} variant="subtitle2">
										{lang.name}
									</MDTypography>
								</MDBox>
								{!hasAssetContentForLanguage(lang.code) && (
									<MDTypography variant="caption">
										{t("ASSETS.DETAILS.contentForThisLanguage")}
									</MDTypography>
								)}
							</Stack>
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<ScrollableContent>
				<RenderAssetDisplay asset={asset} currentLanguage={currentLanguage} />

				{/* Common Information Section (not language specific) */}
				<Box mb={2}>
					<DetailItem>
						<DetailLabel>{t("ASSETS.DETAILS.created_at")}:</DetailLabel>
						<DetailValue>
							{asset?.createdAt ? moment(asset?.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
						</DetailValue>
					</DetailItem>
					<DetailItem>
						<DetailLabel>{t("ASSETS.DETAILS.updated_at")}:</DetailLabel>
						<DetailValue>
							{asset?.updatedAt ? moment(asset?.updatedAt).format("DD/MM/YYYY HH:mm") : "-"}
						</DetailValue>
					</DetailItem>
				</Box>

				{/* Metadatas */}
				<Box mb={2}>
					<DetailItem>
						<Box display="flex" flexDirection="column" width="100%">
							<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
								<DetailLabel>{t("SOURCES.metadatas")}</DetailLabel>

								{editionDisabled && (
									<Tooltip title={t("ASSETS.editMetadatas")} placement="top">
										<IconButton size="small" onClick={() => setEditionDisabled(false)}>
											<Icon fontSize="small" color="info">
												edit
											</Icon>
										</IconButton>
									</Tooltip>
								)}
								{!editionDisabled && (
									<Box display="flex" justifyContent="center" alignItems="center">
										<Tooltip title={t("SETTINGS.cancel")} placement="top">
											<IconButton size="small" onClick={() => cancelMetadatasChanges()}>
												<Icon fontSize="small" color="dark">
													close
												</Icon>
											</IconButton>
										</Tooltip>
										<Tooltip title={t("SETTINGS.save")} placement="top">
											<IconButton size="small" onClick={() => saveMetadatasChanges()}>
												<Icon fontSize="small" color="info">
													check
												</Icon>
											</IconButton>
										</Tooltip>
									</Box>
								)}
							</Box>

							<Box display="flex" flexWrap="wrap" flexDirection="column">
								<MDInput
									disabled={editionDisabled}
									sx={{ mt: 1 }}
									fullWidth
									label="Titre"
									value={metadatasSkeleton.title}
									onChange={e => {
										handleChange("title", e.target.value);
									}}
									InputProps={{
										style: { backgroundColor: editionDisabled ? "transparent" : "white" }
									}}
								/>
								<MDInput
									disabled={editionDisabled}
									sx={{ mt: 1 }}
									fullWidth
									label="Description"
									value={metadatasSkeleton.description}
									onChange={e => {
										handleChange("description", e.target.value);
									}}
									InputProps={{
										style: { backgroundColor: editionDisabled ? "transparent" : "white" }
									}}
								/>

								<MDTypography variant="caption" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
									Métadonnées personnalisées
								</MDTypography>
								{!hasCustomMetadatas(metadatasDictionary?.items || {}) && (
									<MDBox
										p={1}
										display="flex"
										flexDirection="column"
										justifyContent="center"
										alignItems="center"
										sx={{
											width: "100%"
										}}
									>
										<MDTypography variant="caption" color="text">
											Aucune métadonnée personnalisée disponible.
										</MDTypography>
									</MDBox>
								)}
								<LittleForm
									disabled={editionDisabled}
									object={metadatasDictionary}
									metadatasSkeleton={metadatasSkeleton}
									handleChange={handleChange}
								/>
								{/* HTML */}

								<MDBox mt={2} display="flex" flexDirection="column" width="100%">
									<MDTypography variant="caption" fontWeight="bold">
										{t("ASSETS.advancedOptions")}
									</MDTypography>
									{asset?.format === "WebPage" && (
										<MDInput
											disabled={editionDisabled}
											sx={{ mt: 1 }}
											fullWidth
											label={t("SOURCES.METADATAS.htmlSelectorDescription")}
											value={metadatasSkeleton.htmlSelector}
											onChange={e => {
												handleChange("htmlSelector", e.target.value);
											}}
											InputProps={{
												style: { backgroundColor: editionDisabled ? "transparent" : "white" }
											}}
										/>
									)}
									<InputBoolean
										InputProps={{ sx: { mt: 1 } }}
										item={{}}
										disabled={editionDisabled}
										label={t("SOURCES.EXTRACT.extractTextImageTitle")}
										value={metadatasSkeleton.extractTextFromImage}
										onChange={newValue => {
											handleChange("extractTextFromImage", newValue);
										}}
									/>
								</MDBox>
							</Box>
						</Box>
					</DetailItem>
				</Box>
			</ScrollableContent>
		</MDBox>
	);
};

export default DetailContent;
